// A video's or a sound's volume keyframes on its block (spec volume-keyframes): its level as a line
// across the block, from silence at the bottom to 200 % at the top, its keyframes as dots, and the
// part past 100 % amber. Drawn only: the keyframes are set at the playhead in the Parameters' Volume.
import { useId } from "react";

import { MAX_VOLUME, clipMs, volumeAt, volumeMsOf, type AudioClip, type VideoClip } from "./timeline";
import classes from "./volume-line.module.css";

export function VolumeLine(props: { clip: VideoClip | AudioClip }) {
  const { clip } = props;
  const gradient = useId();
  const length = clipMs(clip);
  const inside = clip.volumePoints
    .map((point) => volumeMsOf(clip, point))
    .filter((at) => at > clip.startMs && at < clip.endMs);
  const x = (at: number) => ((at - clip.startMs) / length) * 100;
  const y = (level: number) => (1 - level / MAX_VOLUME) * 100;
  const line = [clip.startMs, ...inside, clip.endMs]
    .map((at) => `${x(at).toFixed(2)},${y(volumeAt(clip, at)).toFixed(2)}`)
    .join(" ");
  return (
    <span className={classes.volumeLine} data-volume-line aria-hidden>
      <svg className={classes.canvas} viewBox="0 0 100 100" preserveAspectRatio="none">
        {/* Past 100 %, the top half, the line is amber. */}
        <linearGradient id={gradient} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2="100">
          <stop offset="0.5" className={classes.boosted} />
          <stop offset="0.5" className={classes.level} />
        </linearGradient>
        <polyline className={classes.line} points={line} stroke={`url(#${gradient})`} />
      </svg>
      {inside.map((at) => (
        <span
          key={at}
          className={classes.dot}
          data-boosted={volumeAt(clip, at) > 1 || undefined}
          style={{ left: `${x(at)}%`, top: `${y(volumeAt(clip, at))}%` }}
        />
      ))}
    </span>
  );
}
